/* ═══════════════════════════════════════════════════════════════
   The Designer — the find & replace bar.

   Word's Ctrl+F, on the paper: a small fixed bar under the top bar,
   live match highlighting as you type, "n of m", prev/next, Replace
   and Replace All. It renders only while the search plugin says the
   search is open, so the toolbar button and Ctrl+F share one truth.

   Esc closes the bar and puts the caret back on the paper. A Replace
   All is one transaction, so one Ctrl+Z brings everything back.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  closeFind,
  getFindState,
  replaceActive,
  replaceAllMatches,
  setFindQuery,
  stepFind,
} from './search';

interface Props {
  editor: Editor;
  canEdit: boolean;
  /** The toolbar cluster the bar hangs below, so it reads as belonging
   *  to the control that opened it. */
  anchorRef: React.RefObject<HTMLElement | null>;
}

/** The active-match decoration, freshly painted by the last transaction. */
function scrollActiveIntoView(editor: Editor) {
  editor.view.dom.querySelector('.dsg-find-active')?.scrollIntoView({ block: 'center' });
}

export function FindReplace({ editor, canEdit, anchorRef }: Props) {
  // Re-render on every editor transaction — match counts and highlighting
  // are plugin state, and the plugin speaks through transactions.
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    editor.on('transaction', force);
    return () => {
      editor.off('transaction', force);
    };
  }, [editor]);

  const find = getFindState(editor.state);
  const open = !!find?.open;

  const [replaceValue, setReplaceValue] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Anchor under the toolbar cluster; follow window resizes while open.
  const [pos, setPos] = React.useState<{ top: number; right: number } | null>(null);
  React.useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      setPos({
        top: r ? r.bottom + 8 : 64,
        right: r ? Math.max(8, window.innerWidth - r.right) : 16,
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, anchorRef]);

  // Opening puts the keyboard in the query box, ready to type over it.
  React.useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [open]);

  if (!open || !find || !pos) return null;

  const dispatch = editor.view.dispatch.bind(editor.view);
  const count = find.matches.length;
  const counter = !find.query ? '' : count ? `${find.activeIndex + 1} of ${count}` : 'No matches';

  const close = () => {
    closeFind(editor.state, dispatch);
    editor.commands.focus();
  };
  const step = (dir: 1 | -1) => {
    stepFind(editor.state, dispatch, dir);
    scrollActiveIntoView(editor);
  };
  const onQuery = (q: string) => {
    setFindQuery(editor.state, dispatch, q);
    scrollActiveIntoView(editor);
  };
  const onReplace = () => {
    if (replaceActive(editor.state, dispatch, replaceValue)) scrollActiveIntoView(editor);
  };
  const onReplaceAll = () => replaceAllMatches(editor.state, dispatch, replaceValue);

  const onBarKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  return createPortal(
    <div
      className="dsg-find"
      role="search"
      aria-label="Find and replace in this spec"
      style={{ top: pos.top, right: pos.right }}
      onKeyDown={onBarKeyDown}
    >
      <div className="dsg-find-row">
        <input
          ref={inputRef}
          className="dsg-input dsg-find-input"
          type="text"
          value={find.query}
          onChange={(e) => onQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              step(e.shiftKey ? -1 : 1);
            }
          }}
          placeholder="Find in this section"
          aria-label="Find"
        />
        <span className="dsg-find-count" role="status" aria-live="polite">
          {counter}
        </span>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={() => step(-1)}
          disabled={!count}
          aria-label="Previous match (Shift+Enter)"
          title="Previous match (Shift+Enter)"
        >
          <ChevronUp size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={() => step(1)}
          disabled={!count}
          aria-label="Next match (Enter)"
          title="Next match (Enter)"
        >
          <ChevronDown size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={close}
          aria-label="Close find (Esc)"
          title="Close find (Esc)"
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>

      {canEdit && (
        <div className="dsg-find-row">
          <input
            className="dsg-input dsg-find-input"
            type="text"
            value={replaceValue}
            onChange={(e) => setReplaceValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onReplace();
              }
            }}
            placeholder="Replace with"
            aria-label="Replace with"
          />
          <button
            type="button"
            className="dsg-find-btn"
            onClick={onReplace}
            disabled={!count}
            title="Replace the current match, then move to the next"
          >
            Replace
          </button>
          <button
            type="button"
            className="dsg-find-btn"
            onClick={onReplaceAll}
            disabled={!count}
            title="Replace every match — one Ctrl+Z undoes all of it"
          >
            Replace all
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}
